<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">List of Ignored URLs</span>
      <a-button size="large" class="a-header--button slide-down-enter-active" type="primary" @click="onAddIgnoredUrl">
        <a-icon type="plus" />
        Add Ignored URL
      </a-button>
    </div>
    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoading">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div v-if="!isLoading && !data.length" class="a-body flex-grow-1 d-flex justify-content-center align-items-center">
        <div class="text-center">
          <a-icon type="inbox" style="font-size: 64px; color: #d9d9d9; margin-bottom: 16px;" />
          <h3 class="text-muted">No Ignored URLs</h3>
          <p class="text-muted">This domain doesn't have any ignored URLs yet.</p>
          <a-button type="primary" @click="onAddIgnoredUrl">
            <a-icon type="plus" />
            Add First Ignored URL
          </a-button>
        </div>
      </div>
    </transition>
    <transition name="slide-up">
      <div v-if="!isLoading && data.length" class="a-filter">
        <a-row class="full-w mt-2" type="flex" justify="start">
          <a-button size="default" :disabled="!isHasSelected" type="danger" class="danger-btn" @click="onBatchDelete()">
            <a-icon type="delete" />
            Delete Selected
          </a-button>
          <a-button class="ml-2" type="primary" size="default" @click="showBatchSelectUrlsModal">
            Batch Select URLs
          </a-button>
          <a-modal
            :closable="false"
            title="Select Ignored URLs"
            width="45vw"
            :visible="isShowBatchSelectUrlsModal"
            @cancel="closeBatchSelectUrlsModal"
            @ok="handleSubmitBatchSelectUrls"
          >
            <a-textarea
              placeholder="Enter URLs to select (one per line):
https://example.com/admin
https://example.com/private
https://example.com/temp"
              :autosize="{ minRows: 12, maxRows: 14 }"
              :value="batchSelectUrls"
              @change="handleChangeBatchSelectUrls"
            />
          </a-modal>
        </a-row>
      </div>
    </transition>
    <div v-resize="elSizeChange" class="a-body flex-grow-1 overflow-y-hidden" v-if="!isLoading && data.length">
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable
            :hasSelectRow="true"
            :pagination="pagin"
            :columns="DomainIgnoredUrlsColumn"
            :dataSource="data"
            :tableSize="tableSize"
            @changeSelectedRow="onSelectChange">
            <template slot="url" slot-scope="{ record }">
              <a-tooltip :title="record.url" placement="topLeft">
                <a :href="record.url" target="_blank" rel="noopener noreferrer" class="url-link">
                  {{ record.url }}
                  <a-icon type="external-link" class="ml-1" style="font-size: 12px;" />
                </a>
              </a-tooltip>
            </template>
            <template slot="operation" slot-scope="{ record }">
              <a-dropdown placement="topLeft">
                <a-icon class="operation" type="ellipsis" />
                <a-menu slot="overlay" class="operation-menu">
                  <a-menu-item @click="onRemoveIgnoredUrl(record)">
                    <img class="mr-2" src="@assets/icon/delete.svg" />
                    Remove
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer" v-if="!isLoading && data.length">
      <PaginationBar :total="pagin.total" @change="paginChange" />
    </div>

    <!-- Add Modal -->
    <a-modal
      title="Add Ignored URLs"
      :visible="isModalVisible"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      :confirmLoading="isModalLoading"
      width="600px">
      <a-form :form="ignoredUrlForm" layout="vertical">
        <a-form-item label="URLs to Ignore" :required="true">
          <a-textarea
            v-decorator="['urls_to_ignore', {
              rules: [
                { required: true, message: 'Please enter at least one URL' }
              ]
            }]"
            placeholder="Enter URLs (one per line):
https://example.com/admin
https://example.com/private
https://example.com/temp"
            :rows="6" />
          <div class="ant-form-explain">
            Enter one URL per line. Each URL will be validated before adding to the ignore list.
          </div>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { DomainIgnoredUrlsColumn } from "../columnsTable/DomainIgnoredUrlsColumn";
import { PAGE_OPTIONS } from '@constants/pageOptions';
import DomainService from "../services/Domain.service";

export default {
  name: 'DomainIgnoredUrls',
  data() {
    return {
      DomainIgnoredUrlsColumn,
      PAGE_OPTIONS,
      data: [],
      filterData: [],
      filterParams: {
        search: ''
      },
      pagin: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      isLoading: true,
      tableSize: {
        height: '',
        width: ''
      },
      domain: {
        id: +this.$route.params.id,
      },
      domainInfo: {},
      // Selection state
      selectedRowKeys: [],
      // Modal state
      isModalVisible: false,
      isModalLoading: false,
      ignoredUrlForm: this.$form.createForm(this),
      // Batch select modal state
      isShowBatchSelectUrlsModal: false,
      batchSelectUrls: ''
    };
  },
  computed: {
    isHasSelected() {
      return this.selectedRowKeys.length > 0;
    }
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    elSizeChange() {},

    async fetchList() {
      try {
        const responseData = await DomainService.fetchDomainIgnoredUrls(this.domain.id);

        // Store domain info
        this.domainInfo = {
          id: responseData.domain_id,
          name: responseData.domain_name,
          lastUpdated: responseData.last_updated
        };

        // Transform the API response to match our table structure
        this.data = (responseData.ignored_urls || []).map((url, index) => ({
          index: index + 1,
          url: url,
          id: `${this.domain.id}-${index}`, // Create a unique ID for each row
          isChecked: false
        }));

        this.pagin.total = this.data.length;
      } catch (error) {
        this.$showErrorMessage('Failed to fetch ignored URLs. Please try again.');
        console.error('Error fetching ignored URLs:', error);
      } finally {
        if (this.isLoading) {
          setTimeout(() => {
            this.isLoading = false;
          }, 300);
        }
      }
    },

    paginChange(pagin) {
      this.pagin = { ...pagin };
    },

    // Selection methods
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
    },

    // Batch select modal methods
    showBatchSelectUrlsModal() {
      const preselectedUrls = this.data.filter((item) => item.isChecked).map((item) => item.url);
      this.batchSelectUrls = preselectedUrls.join('\n');
      this.isShowBatchSelectUrlsModal = true;
    },

    closeBatchSelectUrlsModal() {
      this.isShowBatchSelectUrlsModal = false;
    },

    handleChangeBatchSelectUrls(e) {
      this.batchSelectUrls = e.target.value;
    },

    handleSubmitBatchSelectUrls() {
      try {
        // Find the respective ids of the urls inputted by the user
        const selectedUrls = this.batchSelectUrls.split('\n').filter((url) => url.trim().length > 0);
        const selectedUrlIds = selectedUrls.map((url) => {
          const record = this.data.find((item) => item.url === url.trim())
          if (!record) {
            throw new Error(`URL '${url}' does not exist in the ignored list`);
          } else {
            return record.id;
          }
        });

        // Uncheck the removed urls that were previously checked
        this.selectedRowKeys.forEach((rowKey) => {
          if (!selectedUrlIds.includes(rowKey)) {
            const row = this.data.find((item) => item.id === rowKey);
            if (row) row.isChecked = false;
          }
        });

        // Check the added urls
        selectedUrlIds.forEach((urlId) => {
          const row = this.data.find((item) => item.id === urlId);
          if (row && !row.isChecked) {
            row.isChecked = true;
          }
        });

        this.onSelectChange(selectedUrlIds);
        this.$showSuccessMessage("Batch selected URLs successfully");
        this.closeBatchSelectUrlsModal();
      } catch (err) {
        this.$showErrorMessage(err.message);
      }
    },

    // Batch delete method
    onBatchDelete() {
      const recordsSelected = this.data.filter(item => this.selectedRowKeys.includes(item.id));
      this.$confirm({
        title: `Are you sure you want to remove ${recordsSelected.length} ignored URL(s)?`,
        content: 'The selected URLs will be removed from the ignored list.',
        okText: 'Yes, Remove',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            const urlsToRemove = recordsSelected.map(record => record.url);
            const requestBody = {
              urls_to_remove: urlsToRemove
            };

            const response = await DomainService.removeIgnoredUrls(this.domain.id, requestBody);

            this.$showSuccessMessage(response.message || `${recordsSelected.length} ignored URL(s) removed successfully`);
            this.selectedRowKeys = []; // Clear selection
            this.fetchList(); // Refresh the list
          } catch (error) {
            this.$showErrorMessage('Failed to remove ignored URLs');
            console.error('Error removing ignored URLs:', error);
          }
        }
      });
    },

    // Modal methods
    onAddIgnoredUrl() {
      this.ignoredUrlForm.resetFields();
      this.isModalVisible = true;
    },

    onRemoveIgnoredUrl(record) {
      this.$confirm({
        title: 'Remove Ignored URL',
        content: `Are you sure you want to remove "${record.url}" from the ignored list?`,
        okText: 'Yes, Remove',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            const requestBody = {
              urls_to_remove: [record.url]
            };

            const response = await DomainService.removeIgnoredUrls(this.domain.id, requestBody);

            this.$showSuccessMessage(response.message || 'Ignored URL removed successfully');
            this.fetchList(); // Refresh the list
          } catch (error) {
            this.$showErrorMessage('Failed to remove ignored URL');
            console.error('Error removing ignored URL:', error);
          }
        }
      });
    },

    handleModalOk() {
      this.ignoredUrlForm.validateFields(async (err, values) => {
        if (!err) {
          this.isModalLoading = true;

          try {
            // Parse URLs from textarea (one per line)
            const urlsText = values.urls_to_ignore.trim();
            if (!urlsText) {
              this.$showErrorMessage('Please enter at least one URL');
              this.isModalLoading = false;
              return;
            }

            const urls = urlsText
              .split('\n')
              .map(url => url.trim())
              .filter(url => url.length > 0);

            if (urls.length === 0) {
              this.$showErrorMessage('Please enter at least one valid URL');
              this.isModalLoading = false;
              return;
            }

            // Validate URLs format
            const urlPattern = /^https?:\/\/.+/i;
            const invalidUrls = urls.filter(url => !urlPattern.test(url));

            if (invalidUrls.length > 0) {
              this.$showErrorMessage(`Invalid URL format: ${invalidUrls.join(', ')}`);
              this.isModalLoading = false;
              return;
            }

            const requestBody = {
              urls_to_ignore: urls
            };

            const response = await DomainService.addIgnoredUrls(this.domain.id, requestBody);

            this.$showSuccessMessage(response.message || 'Ignored URLs added successfully');
            this.isModalVisible = false;
            this.fetchList(); // Refresh the list
          } catch (error) {
            if (error.response && error.response.data && error.response.data.errors) {
              // Handle validation errors
              const errors = error.response.data.errors;
              const errorMessages = Object.values(errors).flat();
              this.$showErrorMessage(errorMessages.join(', '));
            } else {
              this.$showErrorMessage('Failed to add ignored URLs');
            }
            console.error('Error saving ignored URLs:', error);
          } finally {
            this.isModalLoading = false;
          }
        }
      });
    },

    handleModalCancel() {
      this.isModalVisible = false;
      this.ignoredUrlForm.resetFields();
    }
  },
};
</script>

<style lang="scss" scoped>
.url-link {
  color: #1890ff;
  text-decoration: none;
  max-width: 550px;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    text-decoration: underline;
  }
}

.danger-btn {
  background-color: #ff4d4f;
  border-color: #ff4d4f;
  color: white;

  &:hover {
    background-color: #ff7875;
    border-color: #ff7875;
  }

  &:disabled {
    background-color: #f5f5f5;
    border-color: #d9d9d9;
    color: rgba(0, 0, 0, 0.25);
  }
}
</style>